/**
 * The point table an intro screen shows (specs/champions-league, Story 8).
 *
 * The intro used to state a flat "exact = 5, outcome = 2, every game" for every non-World-Cup
 * competition. That is true of a season pool and false of every tournament with a stage ladder:
 * the Champions League pays 4/2 in the league phase and climbs to 20/12 in the final, so the
 * screen that exists to explain the rules was teaching the wrong ones.
 *
 * Rows are built from the competition's server-resolved `rules`, which is the same resolved
 * config the settlement path scores with — so the table cannot drift from what is actually paid.
 * When `rules` predates the fields (an older server, or an app opened before the deploy lands),
 * the caller's static rows are used unchanged.
 */

import type { CompetitionRulesSummary } from '../types/competitions.types';
import type { CompetitionIntroPointsRow } from '../types/competitionIntro.types';

/**
 * Generic names for the stage keys, used only when the competition sent no label of its own.
 * A competition that calls its Round of 32 a "Playoff" overrides this through `stageLabels`.
 */
const FALLBACK_STAGE_LABELS: Record<string, string> = {
  REGULAR_SEASON: 'Matchweek',
  GROUP_STAGE: 'Group Stage',
  ROUND_OF_32: 'Round of 32',
  ROUND_OF_16: 'Round of 16',
  QUARTER_FINALS: 'Quarter-Finals',
  SEMI_FINALS: 'Semi-Finals',
  THIRD_PLACE_MATCH: '3rd-Place Match',
  FINAL: 'Final',
};

/** Title-cases an unknown stage key so a new enum value degrades to "New Stage", never "NEW_STAGE". */
function humanizeStageKey(stage: string): string {
  return stage
    .toLowerCase()
    .split('_')
    .filter((word) => word !== '')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function stageLabel(stage: string, rules?: CompetitionRulesSummary | null): string {
  const fromServer = rules?.stageLabels?.[stage];
  if (typeof fromServer === 'string' && fromServer !== '') return fromServer;
  return FALLBACK_STAGE_LABELS[stage] ?? humanizeStageKey(stage);
}

/**
 * One row per stage the competition plays, in its own stage order.
 *
 * Returns an empty array — not a guessed table — when the competition sent no ladder, so the
 * caller can keep its static copy rather than publish invented numbers.
 */
export function buildIntroPointsRows(
  rules?: CompetitionRulesSummary | null,
): CompetitionIntroPointsRow[] {
  const stagePoints = rules?.stagePoints;
  const stageOrder = rules?.stageOrder;
  if (stagePoints == null || !Array.isArray(stageOrder) || stageOrder.length === 0) return [];

  const rows: CompetitionIntroPointsRow[] = [];
  for (const stage of stageOrder) {
    const points = stagePoints[stage];
    if (points == null) continue;
    rows.push({ stage: stageLabel(stage, rules), hit: points.hit, result: points.result });
  }
  return rows;
}

/** True when every stage pays the same — a season pool, or a tournament with a flat ladder. */
export function isFlatLadder(rows: readonly CompetitionIntroPointsRow[]): boolean {
  if (rows.length === 0) return false;
  const [first] = rows;
  return rows.every((row) => row.hit === first.hit && row.result === first.result);
}

/**
 * The one-line "Scoring" rule card, in this competition's real numbers.
 *
 * A flat ladder gets the reassuring version ("every game is worth the same"); a climbing one
 * names both ends, because "the later rounds are worth more" is the single most important thing
 * a tournament's scoring says and the old copy omitted it entirely.
 */
export function scoringRuleDescription(
  rows: readonly CompetitionIntroPointsRow[],
  fallback: string,
): string {
  if (rows.length === 0) return fallback;

  const [first] = rows;
  if (isFlatLadder(rows)) {
    return `Exact score = ${first.hit} points. Right outcome = ${first.result} points. `
      + 'Every game is worth the same.';
  }

  const last = rows[rows.length - 1];
  return `Exact score = ${first.hit} points and the right outcome = ${first.result} in the `
    + `${first.stage.toLowerCase()} rounds. Every round after that is worth more, up to `
    + `${last.hit} and ${last.result} in the ${last.stage}.`;
}

/**
 * The handwritten note under the x2 preview, in this competition's own vocabulary.
 *
 * A stage listed in `multiRoundStages` holds one boost **per round** — the Champions League gives
 * a fresh x2 on each of its eight matchdays — while a stage absent from it holds exactly one.
 * Saying which is which is the whole point of the note: "one ×2 per stage" was the single piece
 * of the World Cup copy that players read wrong.
 */
export function goldenBallNote(
  rules: CompetitionRulesSummary | null | undefined,
  fallback: string,
): string {
  const stages = rules?.goldenBallStages;
  if (!Array.isArray(stages) || stages.length === 0) return fallback;

  const parts = stages.map((stage) => {
    const label = stageLabel(stage, rules);
    const rounds = rules?.multiRoundStages?.[stage];
    if (Array.isArray(rounds) && rounds.length > 1) {
      return `each ${label.toLowerCase()} (${rounds[0]}-${rounds[rounds.length - 1]})`;
    }
    return `the ${label}`;
  });

  const multiplier = rules?.goldenBallMultiplier ?? 2;
  const joined = parts.length === 1
    ? parts[0]
    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  return `One ×${multiplier} on ${joined}`;
}

/**
 * How the intro states the futures deadline, in two fragments: one for the rules card and one
 * for the points note.
 *
 * A competition that locks at its first kickoff reads exactly as it always did. One that holds
 * the window open to the end of a round (the Champions League's opening matchday, AC-15.14)
 * says so instead — the deadline is the last kickoff of that round, not the first of the
 * competition, and a player who reads the old line would think they had already missed it.
 */
export function introFuturesDeadlineCopy(
  lockAfterRound: { stage: string; round: string } | null | undefined,
): { rule: string; note: string } {
  const round = Number(lockAfterRound?.round);
  if (!lockAfterRound || !Number.isFinite(round)) {
    return {
      rule: 'Both lock at the very first kickoff.',
      note: 'chosen before a ball is kicked',
    };
  }
  if (round === 1) {
    return {
      rule: 'You have the whole opening round of games to decide, and both lock when the last '
        + 'of those games kicks off.',
      note: 'chosen while the opening round is still being played',
    };
  }
  return {
    rule: `You have until round ${round} to decide, and both lock when the last game of that `
      + 'round kicks off.',
    note: `chosen no later than round ${round}`,
  };
}
